import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useUserDetails } from "@/hooks/api/useUserDetails";
import { useContactLogsInfinite } from "@/hooks/api/useContactLogs";
import { useContacts } from "@/hooks/api/useContacts";
import { useNavigate } from "react-router-dom";
import { User, Tag } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MemoryCardSkeleton } from "@/components/skeletons/MemoryCardSkeleton";
import { useAnalytics } from "@/hooks/useAnalytics";

const Memories = () => {
  const navigate = useNavigate();
  const { data: userDetails } = useUserDetails();
  const {
    data: contactLogsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useContactLogsInfinite();
  const { data: contactsData } = useContacts();
  const { trackUserInteraction, trackButtonClick } = useAnalytics();

  const firstName = userDetails?.first_name || "there";
  const allMemories = contactLogsData?.pages.flatMap(page => page.results) || [];
  const totalMoments = contactLogsData?.pages[0]?.count || 0;
  const contacts = contactsData?.results || [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getContactRelationship = (contactId: number) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact?.relationship_type;
  };

  const getContactAvatar = (contactId: number) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact?.avatar;
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 md:px-8">
        <div className="py-6 md:py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-medium font-euclid text-wabisabi-plum mb-2">
              Hi {firstName}, here are your memories.
            </h2>
            <p className="text-gray-600">A gentle archive of personal moments and shared experiences.</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3 md:gap-6 mb-8">
            {/* Total Moments Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[11px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">Total moments you've captured</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">{totalMoments}</p>
            </div>

            {/* Active Weeks Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[11px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">How many weeks you've actively logged memories</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">0</p>
            </div>
          </div>

          {/* Memories Section */}
          <div className="mb-8">
            <h3 className="text-[20px] font-bold font-euclid text-wabisabi-plum mb-4">Memories</h3>
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <MemoryCardSkeleton />
                <MemoryCardSkeleton />
                <MemoryCardSkeleton />
                <MemoryCardSkeleton />
                <MemoryCardSkeleton />
                <MemoryCardSkeleton />
              </div>
            ) : allMemories.length > 0 ? (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allMemories.map((memory) => (
                  <div
                    key={memory.id}
                    className="bg-white rounded-lg p-6 h-[224px] flex flex-col cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => {
                      trackUserInteraction('View Memory', memory.contact_name);
                      navigate(`/memories/${memory.id}`);
                    }}
                  >
                    {/* Contact Avatar and Name */}
                    <div className="flex items-center gap-3 mb-4">
                      <Avatar className="w-12 h-12 flex-shrink-0">
                        {getContactAvatar(memory.contact) ? (
                          <AvatarImage
                            src={`/images/avatars/${getContactAvatar(memory.contact)}`}
                            alt={memory.contact_name}
                          />
                        ) : null}
                        <AvatarFallback className="bg-gray-200">
                          <User className="w-6 h-6 text-gray-500" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h4 className="text-[16px] font-bold text-black mb-1">
                          {memory.contact_name}
                        </h4>
                        {getContactRelationship(memory.contact) && (
                          <div className="flex items-center gap-1 text-gray-600">
                            <Tag className="w-3 h-3" />
                            <span className="text-[12px]">
                              {getContactRelationship(memory.contact)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Memory Content */}
                    <p className="text-[14px] text-gray-600 mb-3 flex-1 overflow-hidden line-clamp-4">
                      {memory.content}
                    </p>

                    {/* Date and Mood */}
                    <div className="flex items-center justify-between text-[12px] text-gray-500">
                      <span>{formatDate(memory.log_date)}</span>
                      {memory.mood && (
                        <span className="italic">{memory.mood}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasNextPage && (
                <div className="flex justify-center mt-6">
                  <Button
                    onClick={() => {
                      trackButtonClick('Load More Memories', 'memories');
                      fetchNextPage();
                    }}
                    disabled={isFetchingNextPage}
                    variant="outline"
                    className="min-w-[150px]"
                  >
                    {isFetchingNextPage ? (
                      <Skeleton className="h-4 w-20" />
                    ) : (
                      "Load More"
                    )}
                  </Button>
                </div>
              )}
              </>
            ) : (
              <div className="bg-white rounded-lg p-12 text-center">
                <p className="text-gray-500">No memories yet. Start capturing moments!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Memories;
